import React, { useState } from 'react';
import { PlusIcon, SearchIcon, EditIcon, TrashIcon, PhoneIcon, MailIcon, DownloadIcon, FileSpreadsheetIcon, ShieldIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Modal } from '../ui/Modal';
import { Badge } from '../ui/Badge';
import { useData } from '../../contexts/DataContext';
import { Insurance as InsuranceType } from '../../types';
import { exportInsurancesToExcel } from '../../utils/excelExport';
export function Insurance() {
  const {
    insurances,
    addInsurance,
    updateInsurance,
    deleteInsurance
  } = useData();
  const [searchTerm, setSearchTerm] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedInsurance, setSelectedInsurance] = useState<InsuranceType | null>(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    coverageType: '',
    phone: '',
    email: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isExporting, setIsExporting] = useState(false);
  const filteredInsurances = insurances.filter(i => i.name.toLowerCase().includes(searchTerm.toLowerCase()) || i.company.toLowerCase().includes(searchTerm.toLowerCase()));
  const openNewForm = () => {
    setSelectedInsurance(null);
    setFormData({
      name: '',
      company: '',
      coverageType: '',
      phone: '',
      email: ''
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const openEditForm = (insurance: InsuranceType) => {
    setSelectedInsurance(insurance);
    setFormData({
      name: insurance.name,
      company: insurance.company,
      coverageType: insurance.coverageType,
      phone: insurance.phone,
      email: insurance.email
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const openDeleteConfirm = (insurance: InsuranceType) => {
    setSelectedInsurance(insurance);
    setIsDeleteConfirmOpen(true);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!formData.name) errors.name = 'El nombre es requerido';
    if (!formData.company) errors.company = 'La compañía es requerida';
    if (!formData.coverageType) errors.coverageType = 'El tipo de cobertura es requerido';
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    if (selectedInsurance) {
      updateInsurance(selectedInsurance.id, formData);
    } else {
      addInsurance(formData);
    }
    setIsFormOpen(false);
  };
  const handleDelete = () => {
    if (selectedInsurance) {
      deleteInsurance(selectedInsurance.id);
      setIsDeleteConfirmOpen(false);
    }
  };
  const handleExportToExcel = async () => {
    setIsExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    exportInsurancesToExcel(insurances);
    setIsExporting(false);
  };
  return <div className="space-y-6" data-id="element-958">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-959">
        <div data-id="element-960">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-961">
            Seguros
          </h1>
          <p className="text-gray-500 dark:text-gray-400" data-id="element-962">
            Gestión de seguros médicos
          </p>
        </div>
        <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-964" />} onClick={openNewForm} data-id="element-963">
          Nuevo Seguro
        </Button>
      </div>

      {/* Beautiful Export Card */}
      <Card className="bg-gradient-to-br from-indigo-50 to-violet-50 dark:from-indigo-950/30 dark:to-violet-950/30 border-indigo-200 dark:border-indigo-800" data-id="element-965">
        <div className="flex items-center justify-between" data-id="element-966">
          <div className="flex items-center gap-4" data-id="element-967">
            <div className="w-14 h-14 rounded-xl bg-indigo-500 dark:bg-indigo-600 flex items-center justify-center shadow-lg shadow-indigo-500/20" data-id="element-968">
              <FileSpreadsheetIcon className="w-7 h-7 text-white" data-id="element-969" />
            </div>
            <div data-id="element-970">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1" data-id="element-971">
                Exportar Base de Datos
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-972">
                Descarga todos los seguros en formato Excel
              </p>
              <div className="flex items-center gap-4 mt-2" data-id="element-973">
                <div className="flex items-center gap-1.5 text-sm text-indigo-700 dark:text-indigo-400" data-id="element-974">
                  <ShieldIcon className="w-4 h-4" data-id="element-975" />
                  <span className="font-medium" data-id="element-976">
                    {insurances.length} seguros
                  </span>
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-977">
                  • Incluye: Nombre, Compañía, Cobertura, Contacto
                </div>
              </div>
            </div>
          </div>
          <Button leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-979" />} onClick={handleExportToExcel} isLoading={isExporting} className="shadow-lg shadow-indigo-500/20 hover:shadow-xl hover:shadow-indigo-500/30 transition-all duration-200" data-id="element-978">
            {isExporting ? 'Exportando...' : 'Descargar Excel'}
          </Button>
        </div>
      </Card>

      {/* Search */}
      <Card data-id="element-980">
        <div className="relative" data-id="element-981">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-982" />
          <Input placeholder="Buscar seguros..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" data-id="element-983" />
        </div>
      </Card>

      {/* Insurance List */}
      <Card padding="none" data-id="element-984">
        <div className="overflow-x-auto" data-id="element-985">
          <table className="w-full" data-id="element-986">
            <thead data-id="element-987">
              <tr className="border-b border-gray-200 dark:border-gray-700" data-id="element-988">
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-989">
                  Nombre
                </th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-990">
                  Compañía
                </th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-991">
                  Cobertura
                </th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-992">
                  Contacto
                </th>
                <th className="text-right py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-993">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-994">
              {filteredInsurances.length === 0 ? <tr data-id="element-995">
                  <td colSpan={5} className="py-12 text-center text-gray-500 dark:text-gray-400" data-id="element-996">
                    No se encontraron seguros
                  </td>
                </tr> : filteredInsurances.map(insurance => <tr key={insurance.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50" data-id="element-997">
                    <td className="py-4 px-6" data-id="element-998">
                      <p className="font-medium text-gray-900 dark:text-white" data-id="element-999">
                        {insurance.name}
                      </p>
                    </td>
                    <td className="py-4 px-6 text-gray-900 dark:text-gray-100" data-id="element-1000">
                      {insurance.company}
                    </td>
                    <td className="py-4 px-6" data-id="element-1001">
                      <Badge variant="info" data-id="element-1002">{insurance.coverageType}</Badge>
                    </td>
                    <td className="py-4 px-6" data-id="element-1003">
                      <div className="space-y-1" data-id="element-1004">
                        {insurance.phone && <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1" data-id="element-1005">
                            <PhoneIcon className="w-3 h-3" data-id="element-1006" /> {insurance.phone}
                          </p>}
                        {insurance.email && <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1" data-id="element-1007">
                            <MailIcon className="w-3 h-3" data-id="element-1008" /> {insurance.email}
                          </p>}
                      </div>
                    </td>
                    <td className="py-4 px-6" data-id="element-1009">
                      <div className="flex items-center justify-end gap-2" data-id="element-1010">
                        <button onClick={() => openEditForm(insurance)} className="p-2 text-gray-500 hover:text-sky-600 hover:bg-sky-50 dark:hover:bg-sky-900/20 rounded-lg transition-colors" data-id="element-1011">
                          <EditIcon className="w-4 h-4" data-id="element-1012" />
                        </button>
                        <button onClick={() => openDeleteConfirm(insurance)} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" data-id="element-1013">
                          <TrashIcon className="w-4 h-4" data-id="element-1014" />
                        </button>
                      </div>
                    </td>
                  </tr>)}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Form Modal */}
      <Modal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} title={selectedInsurance ? 'Editar Seguro' : 'Nuevo Seguro'} size="md" data-id="element-1015">
        <form onSubmit={handleSubmit} className="space-y-4" data-id="element-1016">
          <Input label="Nombre del Seguro" value={formData.name} onChange={e => setFormData({
          ...formData,
          name: e.target.value
        })} error={formErrors.name} placeholder="Ej: ARS Humano" data-id="element-1017" />

          <Input label="Compañía" value={formData.company} onChange={e => setFormData({
          ...formData,
          company: e.target.value
        })} error={formErrors.company} placeholder="Ej: Humano Seguros" data-id="element-1018" />

          <Input label="Tipo de Cobertura" value={formData.coverageType} onChange={e => setFormData({
          ...formData,
          coverageType: e.target.value
        })} error={formErrors.coverageType} placeholder="Ej: Completo, Básico, Premium" data-id="element-1019" />

          <div className="grid grid-cols-2 gap-4" data-id="element-1020">
            <Input label="Teléfono" value={formData.phone} onChange={e => setFormData({
            ...formData,
            phone: e.target.value
          })} placeholder="809-555-0000" data-id="element-1021" />
            <Input type="email" label="Correo Electrónico" value={formData.email} onChange={e => setFormData({
            ...formData,
            email: e.target.value
          })} placeholder="info@seguro.com" data-id="element-1022" />
          </div>

          <div className="flex justify-end gap-3 pt-4" data-id="element-1023">
            <Button variant="outline" type="button" onClick={() => setIsFormOpen(false)} data-id="element-1024">
              Cancelar
            </Button>
            <Button type="submit" data-id="element-1025">
              {selectedInsurance ? 'Guardar Cambios' : 'Crear Seguro'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation */}
      <Modal isOpen={isDeleteConfirmOpen} onClose={() => setIsDeleteConfirmOpen(false)} title="Confirmar Eliminación" size="sm" data-id="element-1026">
        <div className="space-y-4" data-id="element-1027">
          <p className="text-gray-600 dark:text-gray-400" data-id="element-1028">
            ¿Está seguro de que desea eliminar el seguro "
            {selectedInsurance?.name}"?
          </p>
          <div className="flex justify-end gap-3" data-id="element-1029">
            <Button variant="outline" onClick={() => setIsDeleteConfirmOpen(false)} data-id="element-1030">
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleDelete} data-id="element-1031">
              Eliminar
            </Button>
          </div>
        </div>
      </Modal>
    </div>;
}